<template>
  <div class="fh-select-cmp" v-form-item-context="false">
    <jw-trigger :disabled="disabled" trigger="click" :popup-container="popupContainer" update-at-scroll v-model:popup-visible="popupVisible" @popup-visible-change="selectVisibleChange" :popup-offset="0" :popup-translate="[0, 0]">
      <div ref="selectContentRef" class="fh-select-content" @click="handleVisible">
        <div class="select-box" :class="[disabled ? 'content-disable' : '', popupVisible ? 'content-active' : '', error ? 'error-select' : 'select-border']">
          <template v-if="selectedList?.length">
            <div :style="{ width: getWidth(true) }">
              <jw-overflow-list>
                <jw-tag size="small" v-for="item in selectedList" :key="item?.value" class="field-show-item" :style="{ backgroundColor: item?.background || 'var(--color-fill-2)', color: item?.color || 'var(--color-text-1)' }">
                  <slot name="label" :data="item">
                    <div class="field-show-item-label overflow-hidden" :class="[error ? 'error-select-label' : '']">
                      {{ item?.label }}
                    </div>
                  </slot>
                  <icon-close v-if="!(disabled || item.disabled || allowClear)" class="field-show-item-close" @click.stop="handleRemove(item)" />
                </jw-tag>
                <template #overflow="{ number }">
                  <jw-tooltip content-class="jw-tooltip-scroll" :content-style="{ maxWidth: '300px', padding: '8px 6px 0 12px' }">
                    <jw-tag class="hand" size="small">+{{ number }}</jw-tag>
                    <template #content>
                      <jw-space wrap>
                        <div v-for="(item, index) in getOverflowItems(number)" :key="index">
                          <jw-tag class="popover-tag" size="small" :color="item?.background || 'var(--color-fill-2)'" :style="{ color: item?.color || 'var(--color-text-1)', wordBreak: 'break-all', whiteSpace: 'normal', height: 'auto' }">{{ item.label }}</jw-tag>
                        </div>
                      </jw-space>
                    </template>
                  </jw-tooltip>
                </template>
              </jw-overflow-list>
            </div>
          </template>
          <template v-else>
            <span class="select-box-placeholder">{{ placeholder }}</span>
          </template>
        </div>
        <div class="select-arrow">
          <icon-up class="arrow-icon" v-if="showEditContent" />
          <icon-down class="arrow-icon" v-else />
        </div>
      </div>
      <template #content>
        <slot name="associationContent">
          <div :style="{ width: getWidth() }">
            <fh-select-content ref="selectContentOptionsRef" :options="options" :field-names="fieldNames" :limit="limit" :exceedLimit="exceedLimit" :selectedList="selectedList" @change="optionChange" :multiple="multiple">
              <template #empty>
                <slot name="empty"> </slot>
              </template>
            </fh-select-content>
          </div>
        </slot>
      </template>
    </jw-trigger>
  </div>
</template>

<script setup>
import { ref, computed, nextTick } from 'vue';
import FhSelectContent from './select-content/select-content.vue';
import { associationEmptyText } from '@/utils/const';
import { useFormItem } from '@arco-design/web-vue';
const props = defineProps({
  modelValue: [String, Number, Array],
  options: {
    type: Array,
    default: () => []
  },
  disabled: {
    type: Boolean,
    default: false
  },
  multiple: {
    // 多选
    type: Boolean,
    default: false
  },
  placeholder: {
    type: String,
    default: '请选择'
  },
  // 当选中值不在选项列表中时的回退函数
  fallbackOption: {
    type: Function,
    default: undefined
  },
  // 是否允许清空
  allowClear: {
    type: Boolean,
    default: false
  },
  // 最大选择数量
  limit: {
    type: Number,
    default: null
  },
  // 最大选择数量回调
  exceedLimit: {
    type: Function,
    default: undefined
  },
  // 自定义value和label
  fieldNames: {
    type: Object,
    default: () => ({ value: 'value', label: 'label' })
  },
  error: {
    type: Boolean,
    default: false
  },
  popupContainer: {
    type: String,
    default: 'body'
  }
});

const { eventHandlers } = useFormItem();

const emit = defineEmits(['update:modelValue', 'change', 'remove', 'popup-visible-change']);
const showEditContent = ref(false);

const popupVisible = ref(false); // 弹出框是否可见

const selectContentRef = ref(null);
const selectContentOptionsRef = ref(null);
// 标准化选项，确保所有选项都有统一的value和label字段
const formatOptions = computed(() => {
  if (!Array.isArray(props.options) || !props.options.length) return [];
  return props.options.map((option) => ({
    ...option,
    value: option[props.fieldNames.value],
    label: option[props.fieldNames.label],
    disabled: option.disabled
  }));
});

//点击内容
const handleVisible = () => {
  if (!props.disabled) {
    popupVisible.value = true;
    nextTick(() => {
      selectContentOptionsRef.value?.searchInputFocus();
    });
  }
};
const selectedList = computed(() => {
  if (!props.modelValue) return [];
  if (!props.multiple) {
    if (props.modelValue) {
      if (formatOptions.value?.length) {
        const selectedItem = formatOptions.value.filter((fItem) => fItem.value == props.modelValue);
        // 检查是否有匹配项，如果没有且提供了fallbackOption，则使用fallbackOption
        if (selectedItem?.length) {
          return selectedItem;
        } else if (props.fallbackOption) {
          const fallbackItem = props.fallbackOption(props.modelValue);
          return fallbackItem ? [fallbackItem] : [];
        } else {
          return [];
        }
      } else if (props.fallbackOption) {
        // 如果没有options列表但有fallbackOption，使用fallbackOption
        const fallbackItem = props.fallbackOption(props.modelValue);
        return fallbackItem ? [fallbackItem] : [{ value: props.modelValue, label: associationEmptyText }];
      } else {
        return [{ value: props.modelValue, label: associationEmptyText }];
      }
    } else {
      return [];
    }
  } else {
    if (props.modelValue?.length) {
      if (formatOptions.value.length) {
        const resultList = props.modelValue
          ?.map((e) => {
            const selectedItem = formatOptions.value.find((fItem) => fItem.value == e);
            if (selectedItem) {
              return selectedItem;
            } else if (props.fallbackOption) {
              return props.fallbackOption(e);
            }
            return null;
          })
          .filter(Boolean); // 过滤掉null值
        return resultList;
      } else {
        const resultList = props.modelValue?.map((e) => {
          if (props.fallbackOption) {
            const fallbackItem = props.fallbackOption(e);
            return fallbackItem || { value: e, label: associationEmptyText };
          }
          return { value: e, label: associationEmptyText };
        });
        return resultList;
      }
    } else {
      return [];
    }
  }
});

// 获取溢出的标签项
const getOverflowItems = (number) => {
  if (!number || typeof number !== 'number' || number <= 0) {
    return [];
  }

  // 确保number是一个正整数
  const count = Math.floor(number);
  // 返回最后count个元素
  return selectedList.value?.slice(-count);
};

const getWidth = (type = null) => {
  if (type) {
    return selectContentRef.value?.offsetWidth - 16 + 'px';
  }
  return selectContentRef.value?.offsetWidth + 'px';
};

// 删除
const handleRemove = (item) => {
  const removeValue = item.value; // 要删除的值
  // 向父组件更新 v-model
  emit('update:modelValue', props.multiple ? props.modelValue.filter((v) => v !== removeValue) : '');
  // 如果还需要额外事件
  emit('remove', item);
};

// 操作选项
const optionChange = (optionsValue) => {
  //   const selectedItem = props.options?.filter((fItem) => fItem.value == optionsValue);
  // selectedList.value = selectedItem;
  emit('update:modelValue', optionsValue || []);
  emit('change', optionsValue || []);
  if (!props.multiple) {
    // 单选
    popupVisible.value = false;
    emit('popup-visible-change', popupVisible.value ?? []);
  }
  eventHandlers.value?.onChange?.(optionsValue); // 自定义表单组件同步信息至form
};

// 上下箭头
const selectVisibleChange = (event) => {
  showEditContent.value = event;
  emit('popup-visible-change', event);
};

const popupVisibleChangeFn = () => {
  popupVisible.value = false;
};
defineExpose({ popupVisibleChangeFn, getWidth });
</script>

<style scoped lang="less">
@import './fh-select.less';
</style>
